import Image from "next/image";
import Link from "next/link";
import { FaDiscord } from "react-icons/fa";
import { FREE_TOOL_LINKS } from "@/lib/tools/content";
import { USE_CASE_CONTENT_MAP } from "@/lib/use-cases/content";
import { useStrings } from "@/lib/i18n/context";
import type { Strings } from "@/lib/i18n/strings";
import LanguageSwitcher from "./LanguageSwitcher";

type FooterLink = {
  name: string;
  href: string;
};

type FooterSection = {
  title: string;
  links: FooterLink[];
};

const buildSections = (f: Strings["footer"]): FooterSection[] => [
  {
    title: f.generateVideos,
    links: [
      { name: "All features", href: "https://crayo.ai/features" },
      { name: "Reddit Story Videos", href: "https://crayo.ai/features/reddit-story" },
      { name: "Fake Text Videos", href: "https://crayo.ai/features/fake-texts" },
      { name: "Auto Clip", href: "https://crayo.ai/features/auto-clip" },
    ],
  },
  {
    title: f.aiTools,
    links: [
      { name: "AI Voiceover", href: "https://crayo.ai/features/ai-voiceover" },
      { name: "AI Images", href: "https://crayo.ai/features/ai-image" },
      { name: "Voice Changer", href: "https://crayo.ai/features/voice-changer" },
      { name: "Face Swap", href: "https://crayo.ai/features/face-swap" },
      { name: "Icon Generator", href: "https://crayo.ai/features/icon-generator" },
      { name: "Speech Enhancer", href: "https://crayo.ai/features/speech-enhancer" },
      { name: "Developer API", href: "https://crayo.ai/features/api" },
    ],
  },
  {
    title: f.editingTools,
    links: [
      { name: "Video Cutter", href: "https://crayo.ai/features/video-cutter" },
      { name: "Video Crop", href: "https://crayo.ai/features/video-crop" },
      { name: "Subtitle Remover", href: "https://crayo.ai/features/subtitle-remover" },
      { name: "Background Remover", href: "https://crayo.ai/features/remove-bg" },
      { name: "YouTube Downloader", href: "https://crayo.ai/features/youtube-downloader" },
    ],
  },
  {
    title: f.useCases,
    links: [
      { name: "All use cases", href: "https://crayo.ai/use-cases" },
      ...Object.entries(USE_CASE_CONTENT_MAP).map(([slug, useCase]) => ({
        name: useCase.navTitle,
        href: `https://crayo.ai/use-cases/${slug}`,
      })),
    ],
  },
  {
    title: f.freeTools,
    links: [
      { name: "All free tools", href: "https://crayo.ai/features/free" },
      ...FREE_TOOL_LINKS.map((tool) => ({ name: tool.title, href: tool.href })),
    ],
  },
  {
    title: f.calculators,
    links: [
      { name: "All calculators", href: "/calculators" },
      { name: "Views to Money", href: "/calculators/views-to-money" },
      { name: "Posts to a Million Views", href: "/calculators/posts-to-a-million-views" },
      { name: "Creator Money", href: "/calculators/creator-money-calculator" },
      { name: "CPM & RPM", href: "/calculators/cpm-rpm-calculator" },
      { name: "Clips From One Video", href: "/calculators/clips-from-one-video" },
    ],
  },
  {
    title: f.learn,
    links: [
      { name: "Glossary", href: "https://crayo.ai/glossary" },
      { name: "Video ideas", href: "https://crayo.ai/ideas" },
      { name: "Blog", href: "https://crayo.ai/blog" },
      { name: "Reviews", href: "https://crayo.ai/reviews" },
      { name: "Compare", href: "https://crayo.ai/compare" },
    ],
  },
  {
    title: f.product,
    links: [
      { name: f.iosApp, href: "https://crayo.ai/ios" },
      { name: "Pricing", href: "https://crayo.ai/pricing" },
      { name: "Team", href: "https://crayo.ai/team" },
      { name: "Content Rewards", href: "/content-rewards" },
      { name: "Affiliate Program", href: "https://crayo.ai/affiliate" },
      { name: "Enterprise", href: "mailto:support@crayo.ai" },
    ],
  },
  {
    title: f.legal,
    links: [
      { name: "Legal hub", href: "https://crayo.ai/legal" },
      { name: "Company verification", href: "https://crayo.ai/company-verification" },
      { name: "Refund policy", href: "https://crayo.ai/refund-policy" },
      { name: "Terms of service", href: "https://crayo.ai/tos" },
      { name: "Privacy policy", href: "https://crayo.ai/privacy-policy" },
      { name: "Affiliate TOS", href: "https://crayo.ai/affiliate-tos" },
    ],
  },
];

const socials = [
  {
    name: "X",
    href: "https://x.com/CrayoAI",
    icon: (
      <Image
        src="https://cdn-crayo.com/lp/public/footer-x.png"
        alt="X"
        width={0}
        height={0}
        className="size-5"
        style={{
          filter: "invert(1)",
          userSelect: "none",
          pointerEvents: "none",
          touchAction: "none",
        }}
      />
    ),
  },
  {
    name: "Discord",
    href: "https://crayo.ai/discord",
    icon: <FaDiscord className="size-6 text-white" />,
  },
];

const Footer = () => {
  const s = useStrings();
  const sections = buildSections(s.footer);
  return (
    <div className="flex w-full flex-col items-center space-y-8 bg-[#2A66FF] p-6 sm:p-14">
      <footer className="mx-auto w-full max-w-screen-2xl rounded-3xl border border-[#E0E5EA] bg-white px-6 pb-14 shadow-sm lg:px-8">
        <div className="grid grid-cols-2 gap-8 py-12 pl-1 sm:grid-cols-3 lg:grid-cols-4">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-lg font-bold text-black">{section.title}</h3>
              <ul role="list" className="mt-1.5 space-y-1.5">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="text-[13px] text-gray-700 hover:text-gray-900">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <LanguageSwitcher />
        <div className="flex items-center justify-between pt-8">
          <Link
            href="/"
            className="w-1/4"
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                document.getElementById("top")?.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            <Image
              src="https://cdn-crayo.com/lp/public/crayoBlue.png"
              alt="Crayo Logo"
              width={126}
              height={28.5}
              draggable={false}
              style={{
                userSelect: "none",
                pointerEvents: "none",
                touchAction: "none",
              }}
            />
          </Link>
          <div className="flex items-center gap-2.5">
            {socials.map((social) => (
              <Link
                key={social.name}
                {...(social.href === "https://crayo.ai/discord" && { prefetch: false })}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex size-11 items-center justify-center rounded-full bg-[#2A66FF] p-2.5">
                  {social.icon}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
